import type {
  BulkImportResult,
  BulkLimits,
  BulkPreview,
  BulkRowBase,
} from "../../../shared/bulkUpload";

/** The markers a POI can be drawn with (`icon` of `POST /pois`). */
export const POI_ICONS = [
  "COMPANY",
  "HOSPITAL",
  "SCHOOL",
  "CRITICAL",
  "GAS",
  "HOUSE",
  "INFO",
  "MAJOR",
  "MINOR",
  "TRAFFIC",
] as const;

export type PoiIcon = (typeof POI_ICONS)[number];

/** API limits: a name up to 150 characters, remarks up to 500. */
export const POI_NAME_MAX = 150;
export const POI_REMARKS_MAX = 500;

/** A point of interest: one named spot on the map of the user's company. */
export interface Poi {
  readonly id: string;
  readonly name: string;
  readonly latitude: number;
  readonly longitude: number;
  readonly icon: PoiIcon;
  readonly remarks: string | null;
  readonly accountId: string;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** `GET /pois`: one page of the POI list, newest first. A client user only
 *  ever gets their own company's. */
export interface PoiListQuery {
  readonly page: number;
  readonly limit: number;
  /** Part of a POI name, any case; empty or left out for every POI. */
  readonly search?: string;
}

/** `GET /pois/map`: every POI at once, for the map layer. */
export interface PoiMapQuery {
  readonly search?: string;
}

/** `GET /pois/export`: every POI the list shows, in one Excel file. */
export interface PoiExportQuery {
  readonly search?: string;
}

/** `POST /pois`. The POI goes into the logged-in client user's own company:
 *  there's no company to send. */
export interface CreatePoiInput {
  readonly name: string;
  readonly latitude: number;
  readonly longitude: number;
  readonly icon: PoiIcon;
  readonly remarks?: string;
}

/** `PATCH /pois/:poiId`: only what changed. Latitude and longitude go
 *  together; `remarks: null` clears the remarks. An empty input changes
 *  nothing. */
export interface UpdatePoiInput {
  readonly name?: string;
  readonly latitude?: number;
  readonly longitude?: number;
  readonly icon?: PoiIcon;
  readonly remarks?: string | null;
}

/** A saved POI, with the server's success message in the request language. */
export interface SavedPoi {
  readonly poi: Poi;
  readonly message?: string;
}

/** Bulk Upload's limits, as the API enforces them: one `.xlsx` of at most
 *  20 MB holding at most 5,000 records. */
export const POI_BULK_LIMITS: BulkLimits = {
  fileExtension: ".xlsx",
  maxFileMb: 20,
  maxRecords: 5000,
};

/** One row of an uploaded sheet, its values as read (so an invalid one may
 *  hold a coordinate that isn't a number, or an icon that isn't one). */
export interface PoiBulkRow extends BulkRowBase {
  readonly name: string | null;
  readonly latitude: number | string | null;
  readonly longitude: number | string | null;
  readonly icon: string | null;
  readonly remarks: string | null;
}

/** `POST /pois/bulk-upload/preview`: every record of a sheet checked,
 *  nothing created. */
export type PoiBulkPreview = BulkPreview<PoiBulkRow>;

/** `POST /pois/bulk-upload`: the rows still valid were created. */
export type PoiBulkImportResult = BulkImportResult<PoiBulkRow>;
